import React from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  alpha,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BusinessIcon from '@mui/icons-material/Business';

interface POSColors {
  bg: string;
  surface: string;
  surfaceLight: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accent: string;
  accentLight: string;
}

export type SaleMode = 'b2c' | 'b2b';

interface B2BModeToggleProps {
  mode: SaleMode;
  onModeChange: (mode: SaleMode) => void;
  colors: POSColors;
  disabled?: boolean;
}

export const B2BModeToggle: React.FC<B2BModeToggleProps> = ({
  mode,
  onModeChange,
  colors,
  disabled = false,
}) => {
  const handleModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: SaleMode | null
  ) => {
    // Don't allow deselection (null) - always have one mode selected
    if (newMode !== null) {
      onModeChange(newMode);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          color: colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        Sale Mode
      </Typography>
      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={handleModeChange}
        aria-label="sale mode"
        disabled={disabled}
        size="small"
        sx={{
          backgroundColor: colors.surface,
          borderRadius: '10px',
          border: `1px solid ${colors.border}`,
          '& .MuiToggleButtonGroup-grouped': {
            margin: '3px',
            border: 0,
            borderRadius: '8px !important',
            '&:not(:first-of-type)': {
              borderRadius: '8px !important',
              marginLeft: '2px',
            },
            '&:first-of-type': {
              borderRadius: '8px !important',
            },
          },
          '& .MuiToggleButton-root': {
            color: colors.textMuted,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '12px',
            padding: '6px 12px',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: alpha(colors.accent, 0.1),
            },
            '&.Mui-selected': {
              backgroundColor: colors.accent,
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: colors.accentLight,
              },
            },
            '&.Mui-disabled': {
              color: alpha(colors.textMuted, 0.5),
            },
          },
        }}
      >
        <ToggleButton value="b2c" aria-label="B2C mode">
          <Tooltip title="Retail / Consumer Sales" placement="bottom" arrow>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <StorefrontIcon sx={{ fontSize: 16 }} />
              <span>B2C</span>
            </Box>
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="b2b" aria-label="B2B mode">
          <Tooltip title="Business / Reseller Sales" placement="bottom" arrow>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <BusinessIcon sx={{ fontSize: 16 }} />
              <span>B2B</span>
            </Box>
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
      {mode === 'b2b' && (
        <Typography
          sx={{
            fontSize: '10px',
            color: colors.accent,
            fontWeight: 500,
            mt: 0.25,
          }}
        >
          Reseller pricing active
        </Typography>
      )}
    </Box>
  );
};

export default B2BModeToggle;
